import * as React from 'react';
import { findBlurRelatedTarget } from './DomUtil';
import { ElemAttrs } from './HtmlAttrs';

function handleFocus(e: React.FocusEvent<HTMLElement>): void {
  e.stopPropagation(); // just for TFS 739813 and 740525, c.f. PortalEventTrap
}

function handleBlur(e: React.FocusEvent<HTMLElement>, onTrap?: React.FocusEventHandler): void {
  const relatedTarget = findBlurRelatedTarget(e);
  if (relatedTarget && !e.currentTarget.contains(relatedTarget)) {
    (e.target as HTMLElement).focus();
    e.stopPropagation();

    if (onTrap) {
      onTrap(e);
    }
  }
}

export type FocusTrapProps = { onTrap?: React.FocusEventHandler<HTMLElement> } & Omit<ElemAttrs, 'onBlur' | 'onFocus'>;

/**
 * Prevents focus from leaving a container by re-focusing the previously focused element if it is determined that focus would leave the container.
 * Note: this does not work in firefox, which would require a settimeout() before re-focusing.
 * If this ever gets re-enabled for firefox, make sure to re-enable the unit tests.
 */
export const FocusTrap: React.FunctionComponent<FocusTrapProps> = (props) => {
  const { onTrap, ...htmlAttrs } = props;
  const handleBlurCallback = React.useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      handleBlur(e, onTrap);
    },
    [onTrap],
  );

  return <div {...htmlAttrs} onBlur={handleBlurCallback} onFocus={handleFocus} data-focus-trap='' />;
};
